import React, { useState } from 'react';
import { Card, Form, Input, Button, message, Typography, Alert } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthDispatch } from '../context/AuthContext';
import Cookies from 'universal-cookie';
import { cookieOptions } from '../utils/cookieConfig';
import nodeAPI from '../service/ApiServiceNode';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import { jwtDecode } from 'jwt-decode';
import './Login.scss';
import { getApiErrorMessage, getRateLimitMessage } from '../utils/errorUtils';
import logger from '../utils/logger';



const { Title } = Typography;

const Login = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const from = useLocation().state?.from;
    const pathname = from?.pathname;
    const destination = typeof pathname === 'string' && pathname.startsWith('/') && !pathname.startsWith('//') && !pathname.includes('\\')
        ? `${pathname}${from.search || ''}${from.hash || ''}`
        : '/';
    const dispatch = useAuthDispatch();
    
    const isDevelopment = (window.location.hostname === 'localhost' || 
                          window.location.hostname === '127.0.0.1' ||
                          window.location.hostname.includes('netlify.app')) &&
                          process.env.VITE_ENV !== 'production';
    
    const isProduction = !isDevelopment || process.env.VITE_ENV === 'production';
    const googleClientId = process.env.VITE_GOOGLE_CLIENT_ID;

    const handleGoogleSuccess = async (credentialResponse) => {
        // Decode the Google credential JWT to extract the user email for Sentry
        // error attribution. This is best-effort: auth continues normally if
        // decoding fails. jwtDecode does not verify the signature.
        let googleEmail;
        try {
            const decoded = jwtDecode(credentialResponse.credential);
            googleEmail = decoded?.email || undefined;
        } catch (_e) {
            // Ignore — credential decoding failure does not affect login flow
        }

        setLoading(true);
        try {
            logger.debug('Google login successful, sending to backend');

            const response = await nodeAPI.post('/auth/google', {
                credential: credentialResponse.credential
            });

            logger.debug('Google login response:', response.data);

            if (response.data && response.data.statusCode?.code === 1) {
                const { token, user } = response.data.data;

                // Use same R-cookies as DevLogin for consistency
                const cookies = new Cookies();
                cookies.set("tokenR", token, cookieOptions);
                cookies.set("userR", user, cookieOptions);
                cookies.set("userRId", user.id, cookieOptions);

                // Also update AuthContext (which now only uses R-cookies)
                dispatch({
                    type: 'login',
                    payload: {
                        userId: user.id,
                        token: token,
                        user: user
                    }
                });

                navigate(destination, { replace: true });
            } else {
                logger.error('Google login failed - unexpected response:', response.data);
                message.error(response.data?.message || 'Google login failed');
            }
        } catch (error) {
            // Attach the attempted email to Sentry scope before the exception is
            // captured so that the event includes the affected user identity.
            if (googleEmail) {
                Sentry.setUser({ email: googleEmail });
            }
            logger.error('Google login error:', error);
            const rateLimitMsg = getRateLimitMessage(error);
            const errorMessage = rateLimitMsg || getApiErrorMessage(error, 'Google login failed');
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleError = () => {
        message.error('Google login failed');
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            logger.debug('Attempting login with email:', values.email);
            logger.debug('API URL:', process.env.VITE_API_URL || 'http://localhost:3000');

            const response = await nodeAPI.post('/auth/login', {
                email: values.email,
                password: values.password
            });

            logger.debug('Login response:', response.data);

            if (response.data && response.data.statusCode?.code === 1) {
                const { token, user } = response.data.data;

                // Use same R-cookies as DevLogin for consistency
                const cookies = new Cookies();
                cookies.set("tokenR", token, cookieOptions);
                cookies.set("userR", user, cookieOptions);
                cookies.set("userRId", user.id, cookieOptions);

                // Also update AuthContext (which now only uses R-cookies)
                dispatch({
                    type: 'login',
                    payload: {
                        userId: user.id,
                        token: token,
                        user: user
                    }
                });
                
                navigate(destination, { replace: true });
            } else {
                logger.error('Login failed - unexpected response:', response.data);
                message.error('Login failed - unexpected response format');
            }
        } catch (error) {
            // Attach the attempted email to Sentry scope before the exception is
            // captured so that the event includes the affected user identity.
            Sentry.setUser({ email: values.email });
            logger.error('Login error full details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                statusText: error.response?.statusText
            });

            // Show user-friendly rate-limit message, otherwise fall back to detailed error
            const rateLimitMsg = getRateLimitMessage(error);
            const errorMessage = rateLimitMsg || getApiErrorMessage(error, `Login failed (${error.response?.status || 'Network error'})`);
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Always render Google login when Google Client ID is available
    if (googleClientId) {
        return (
            <GoogleOAuthProvider clientId={googleClientId}>
                <div className="google-login-page">
                    <div className="login-left-image"></div>
                    <div className="login-right-content">
                        <div className="login-form">
                            <div className="logo">
                                <img src="/logo-mediatropy-2024-name.gif" alt="Mediatropy" />
                            </div>
                            <h1 className="title">Come on in!</h1>
                            <p className="subtitle">You&apos;ll find no better place to manage your office affairs</p>
                            <div className="google-button-wrapper">
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={handleGoogleError}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </GoogleOAuthProvider>
        );
    }

    // Render standard login only if Google Client ID is not configured (fallback)
    return (
        <div className="login-container">
            <Card className="login-card">
                <div className="login-header">
                    <Title level={2}>HR Platform Login</Title>
                </div>
                
                {!googleClientId && isProduction && (
                    <Alert
                        message="Configuration Required"
                        description="Google OAuth is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID environment variable."
                        type="warning"
                        showIcon
                        style={{ marginBottom: '20px' }}
                    />
                )}
                
                <Form
                    name="login"
                    onFinish={handleSubmit}
                    layout="vertical"
                    size="large"
                >
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: 'Please input your email!' },
                            { type: 'email', message: 'Please enter a valid email!' }
                        ]}
                    >
                        <Input 
                            prefix={<UserOutlined />} 
                            placeholder="Enter your email"
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[{ required: true, message: 'Please input your password!' }]}
                    >
                        <Input.Password 
                            prefix={<LockOutlined />} 
                            placeholder="Enter your password"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={loading}
                            block
                            icon={<LoginOutlined />}
                            className="login-button"
                        >
                            Login
                        </Button>
                    </Form.Item>
                </Form>

            </Card>
        </div>
    );
};

export default Login;
